import { ChevronSmallRightIcon as ChevronRightIcon } from "@wags/ui/product-icons.tsx";

import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { IconSlot } from "@/components/shared/IconSlot";
import { hasLocationContent } from "./location-content";
import {
    joinParts,
    listingHasDelivery,
    type ListingData,
    PinGlyph,
    PlaneGlyph,
    toTransportModuleView,
    TransportModule,
    VanGlyph,
} from "@/features/listings";
import { LISTING_STATIC_MAP } from "@/features/viewer-location";
import {
    factState,
    type Fact,
    sentenceCaseFactLabel,
} from "@/features/providers";
import { ClipboardGlyph, HouseGlyph } from "@/features/listings";

const SERIF_TITLE = "text-section-title text-doberman-black";

const SCREENING_LIST_FORMAT = new Intl.ListFormat("en", {
    type: "conjunction",
});

function OptionRow({
    icon,
    label,
    sub,
    price,
}: {
    icon: ReactNode;
    label: string;

    sub?: string;
    price?: string;
}) {
    return (
        <div className="flex items-center gap-3 py-3.5">
            <IconSlot className="text-chocolate-lab">{icon}</IconSlot>
            <div className="min-w-0 flex-1">
                <p className="text-body font-semibold text-doberman-black">
                    {label}
                </p>
                {sub && (
                    <p className="text-body-small text-doberman-black/55">
                        {sub}
                    </p>
                )}
            </div>
            {price && (
                <p className="shrink-0 text-right text-body font-semibold text-doberman-black">
                    {price}
                </p>
            )}
        </div>
    );
}

function transportIcon(label: string) {
    const text = label.toLowerCase();
    if (
        text.includes("flight") ||
        text.includes("fly") ||
        text.includes("nanny") ||
        text.includes("air")
    ) {
        return <PlaneGlyph className="size-5" />;
    }
    if (
        text.includes("deliver") ||
        text.includes("drop") ||
        text.includes("transport") ||
        text.includes("ship") ||
        text.includes("van")
    ) {
        return <VanGlyph className="size-5" />;
    }
    return <HouseGlyph className="size-5" />;
}

function ScreeningRow({
    screeningSub,
    providerId,
}: {
    screeningSub?: string;
    providerId?: string;
}) {
    if (!screeningSub) return null;
    if (!providerId) {
        return (
            <OptionRow
                icon={<ClipboardGlyph className="size-5" />}
                label="Before you reach out"
                sub={screeningSub}
            />
        );
    }
    return (
        <Link
            to="/provider/$providerId"
            params={{ providerId }}
            hash="requirements"
            className="flex items-center gap-3 rounded py-3.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-labrador-yellow focus-visible:ring-offset-2"
        >
            <IconSlot className="text-chocolate-lab">
                <ClipboardGlyph className="size-5" />
            </IconSlot>
            <span className="min-w-0 flex-1">
                <span className="block text-body font-semibold text-doberman-black">
                    Before you reach out
                </span>
                <span className="block text-body-small text-doberman-black/55">
                    {screeningSub}
                </span>
            </span>
            <span className="shrink-0 text-doberman-black/40">
                <ChevronRightIcon className="size-3.5" />
            </span>
        </Link>
    );
}

function screeningSummary(screeningFacts?: Fact[]) {
    const statedScreening =
        screeningFacts?.filter((fact) => factState(fact) === "yes") ?? [];
    const unknownScreening =
        screeningFacts?.filter((fact) => factState(fact) === "unknown") ?? [];
    return joinParts([
        ...statedScreening.map((fact) => fact.label),
        unknownScreening.length > 0 &&
            `${SCREENING_LIST_FORMAT.format(
                unknownScreening.map((fact) =>
                    sentenceCaseFactLabel(fact.label),
                ),
            )} unconfirmed`,
    ]);
}

export function LocationSection({
    data,
    onAskKits,
    screeningFacts,
}: {
    data: ListingData;
    onAskKits?: () => void;

    screeningFacts?: Fact[];
}) {
    const structured = data.transports ?? [];

    const screeningSub = screeningSummary(screeningFacts);
    const place = joinParts([data.city, data.distanceLabel]);
    const title = "Pickup & transport";
    const transports = [
        ...new Map(
            structured.map((option) => [JSON.stringify(option), option]),
        ).values(),
    ];
    const hasDelivery = listingHasDelivery(data.transports);

    const model = data.transportModel;
    const usePlanner = Boolean(model?.hasAny);

    const screeningRow = (
        <ScreeningRow
            screeningSub={screeningSub}
            providerId={data.providerId}
        />
    );

    const canAskKits = !hasDelivery && Boolean(onAskKits);
    if (
        !hasLocationContent({
            canAskKits,
            hasMap: Boolean(data.mapSrc),
            hasPlanner: usePlanner,
            hasScreening: Boolean(screeningSub),
            transportCount: transports.length,
        })
    ) {
        return null;
    }

    return (
        <div>
            {!usePlanner && data.mapSrc && (
                <div
                    className={`img-inset relative w-full overflow-hidden rounded-card bg-havanese ${LISTING_STATIC_MAP.aspectClass}`}
                >
                    <img
                        src={data.mapSrc}
                        alt={
                            data.city
                                ? `Map of ${data.city}`
                                : "Map of the pickup area"
                        }
                        width={LISTING_STATIC_MAP.width}
                        height={LISTING_STATIC_MAP.height}
                        className="h-full w-full object-cover"
                        loading="lazy"
                    />
                    {place && (
                        <span className="absolute bottom-3 left-3 flex items-center gap-1.5 rounded-label bg-true-white px-3.5 py-1.5 text-body-small text-doberman-black">
                            <PinGlyph className="size-3.5 shrink-0 text-doberman-black/70" />
                            {place}
                        </span>
                    )}
                </div>
            )}
            <div className={usePlanner ? "" : "pt-4"}>
                <h2 className={SERIF_TITLE}>{title}</h2>
                {screeningSub && <div className="mt-1">{screeningRow}</div>}
                {usePlanner && model ? (
                    <div className="mt-4">
                        <TransportModule
                            view={toTransportModuleView(model, {
                                mapSrc: data.mapSrc,
                                city: data.city,
                                distanceLabel: data.distanceLabel,
                                distanceMiles: data.distanceMiles,
                            })}
                        />
                    </div>
                ) : (
                    <>
                        {transports.length > 0 && (
                            <div className="mt-1.5 flex flex-col divide-y divide-doberman-black/10">
                                {transports.map((option) => (
                                    <OptionRow
                                        key={JSON.stringify(option)}
                                        icon={transportIcon(option.label)}
                                        label={option.label}
                                        sub={option.sub}
                                        price={option.price}
                                    />
                                ))}
                            </div>
                        )}
                        {canAskKits && onAskKits && (
                            <button
                                type="button"
                                onClick={onAskKits}
                                className="mt-1.5 flex min-h-11 w-full items-center gap-3 rounded border-t border-doberman-black/10 py-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-labrador-yellow focus-visible:ring-offset-2"
                            >
                                <IconSlot className="text-chocolate-lab">
                                    <VanGlyph className="size-5" />
                                </IconSlot>
                                <span className="flex-1 text-body-small text-doberman-black/70">
                                    Not nearby? Ask Kits about delivery options.
                                </span>
                                <span className="shrink-0 text-doberman-black/40">
                                    <ChevronRightIcon className="size-3.5" />
                                </span>
                            </button>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
